import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  OnInit,
  signal
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  PremiumFieldsSchema,
  PremiumPartnerModel,
  UnifyOptionType,
  YearOptions
} from '@cpd-app/core-module/models';
import {
  E164_REGEX,
  TERM_MONTH_MAP
} from '@cpd-app/core-module/constants/connect.const';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonModule } from '@cpd-app/shared-module/components/button/button.module';
import { PremiumPartnerInputComponent } from '@cpd-app/core-module/components/premium-partner-modal/premium-partner-input/premium-partner-input.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import {
  PREMIUM_PARTNER_UNDER_18_MESSAGE,
  premiumPartnerAge
} from './premium-partner-age';

@Component({
  templateUrl: './premium-partner-modal.component.html',
  styleUrls: ['./premium-partner-modal.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FontAwesomeModule,
    ButtonModule,
    PremiumPartnerInputComponent,
    MatIcon
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PremiumPartnerModalComponent implements OnInit {
  protected readonly formTemplate = signal<PremiumFieldsSchema[]>([]);
  protected partnerFormGroup: FormGroup = new FormGroup({});
  protected partnerFormValue = toSignal(this.partnerFormGroup.valueChanges);
  protected isPartnerFormValid = computed(() => {
    const formKeys = Object.keys(this.partnerFormValue());
    return (
      this.partnerFormGroup.valid &&
      !formKeys.some((formKey) => !this.partnerFormValue()[formKey])
    );
  });
  /**
   * CONNECT.1 (gate D2). A birth date under 18 sends nothing to the partner,
   * and the member is told why rather than left with a button that will not
   * press. Their school requests, if any, are unaffected: Skip still works.
   */
  protected readonly isUnder18 = computed(() => {
    const age = premiumPartnerAge(this.partnerFormValue());
    return age !== null && age < 18;
  });
  protected readonly under18Message = PREMIUM_PARTNER_UNDER_18_MESSAGE;
  protected readonly partnerValue = this.data.partner;
  protected readonly isDisableSkip = this.data.disableSkip;

  public constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      partner: PremiumPartnerModel;
      disableSkip: boolean;
    },
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<PremiumPartnerModalComponent>
  ) {}

  public ngOnInit(): void {
    this.buildDynamicForm();
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected onShareAndConnect(): void {
    // The button is only styled disabled, so the click still arrives.
    if (!this.isPartnerFormValid() || this.isUnder18()) {
      return;
    }
    this.dialogRef.close({
      premiumPartnerId: this.partnerValue.id,
      additionalData: this.partnerFormValue()
    });
  }

  protected onSkip(): void {
    this.dialogRef.close(null);
  }

  private buildDynamicForm(): void {
    const inputMapping = {};

    this.data.partner.additionalFieldsSchema.fields.forEach((field) => {
      this.partnerFormGroup.addControl(
        field.key,
        this.formBuilder.control(field.type === 'tel' ? '+1' : '', {
          validators: this.buildValidators(field),
          nonNullable: true
        })
      );

      if (!Array.isArray(field.options) && field?.options) {
        if (field.options?.type === 'range') {
          field.options = this.generateYearOptions(field.options);
        } else {
          field.options = this.generateTermOptions(
            field.options.cadence,
            field.options.count
          );
        }
      }

      if (field.group) {
        if (!inputMapping[field.group]) {
          inputMapping[field.group] = {
            group: field.group,
            groupLabel: field.groupLabel,
            fields: []
          };
        }
        inputMapping[field.group].fields.push(field);
      } else {
        inputMapping[field.key] = field;
      }
    });

    this.formTemplate.set(Object.values(inputMapping));
  }

  private buildValidators(field: PremiumFieldsSchema): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (field.required) {
      validators.push(Validators.required);
    }

    if (field.type === 'tel') {
      validators.push(Validators.pattern(E164_REGEX));
    }

    return validators;
  }

  private generateYearOptions(fieldOptions: YearOptions): UnifyOptionType[] {
    return Array.from(
      { length: fieldOptions.to - fieldOptions.from + 1 },
      (_, i) => {
        const year = String(fieldOptions.from + i);
        return { value: year, label: year.toString() };
      }
    ).reverse();
  }

  private generateTermOptions(
    cadence: string[],
    count: number
  ): UnifyOptionType[] {
    const currentTerm = cadence.find((term) =>
      TERM_MONTH_MAP[term]?.includes(new Date().getMonth())
    );
    const currentYear = new Date().getFullYear();
    const results: { value: string; label: string }[] = [];

    let currentTermIndex = cadence.indexOf(currentTerm);
    let year = currentYear;

    const totalTerms = count * cadence.length;

    for (let i = 0; i <= totalTerms; i++) {
      const term = cadence[currentTermIndex];

      results.push({
        value: `${term} ${year}`,
        label: `${term} ${year}`
      });

      currentTermIndex++;

      if (currentTermIndex === cadence.length) {
        currentTermIndex = 0;
        year++;
      }
    }

    return results;
  }
}
